<template>
    <div class="ai-preview" :class="'mode-' + mode">
        <div class="ai-preview-head">
            <span class="ai-preview-label">{{ $t("ai_offer.preview_label") }}</span>
            <button type="button" class="ai-preview-close" :aria-label="$t('ai_offer.preview_hide')" @click="$emit('close')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true">
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                </svg>
            </button>
        </div>

        <div class="ai-paper">
            <div class="ai-paper-top">
                <span class="ai-paper-firm">{{ firma || $t("ai_offer.preview_firm") }}</span>
            </div>
            <div class="ai-paper-intro">
                <div class="ai-paper-kicker">{{ $t("ai_offer.preview_kicker") }}</div>
                <div class="ai-paper-title">{{ titleText }}</div>
                <div class="ai-paper-client">
                    {{ $t("ai_offer.preview_for") }} <b>{{ klient || $t("ai_offer.preview_no_client") }}</b>
                </div>
                <div v-if="vzor" class="ai-paper-vzor">{{ $t("ai_offer.preview_template", { nazov: vzor }) }}</div>

                <div class="ai-paper-boxes">
                    <div v-for="i in 3" :key="i" class="ai-paper-box">
                        <span>{{ $t("ai_offer.preview_group", { n: i }) }}</span>
                        <b>…</b>
                    </div>
                    <div class="ai-paper-box total">
                        <span>{{ $t("ai_offer.preview_total") }}</span>
                        <b>{{ totalText }}</b>
                    </div>
                </div>
            </div>

            <div class="ai-paper-rows">
                <div v-for="it in rows" :key="it.key" class="ai-paper-row" :class="{ known: it.known }">
                    <template v-if="it.known">
                        <span class="n">{{ it.nazov }}</span>
                        <span class="src">{{ $t("ai_offer.preview_from_list") }}</span>
                    </template>
                    <template v-else>
                        <span class="skel" :style="{ width: it.width }"></span>
                        <span class="skel short"></span>
                    </template>
                </div>
            </div>
            <p class="ai-paper-note">{{ $t("ai_offer.preview_note") }}</p>
        </div>
    </div>
</template>

<script>
// Priblizny nahlad ponuky z toho, co je vo formulari. Polozky a ceny doplni az AI,
// tu su len navrhnute polozky z cennika a miesto pre zvysok.
const POCET_RIADKOV = { "3-5": 4, "6-10": 7, "11-20": 10, neriesit: 6 };

export default {
    name: "AiOfferPreview",
    props: {
        mode: { type: String, default: "side" },
        firma: { type: String, default: "" },
        klient: { type: String, default: "" },
        zadanie: { type: String, default: "" },
        rozpocet: { type: [String, Number], default: "" },
        mena: { type: String, default: "€" },
        polozky: { type: Array, default: () => [] },
        pocet: { type: String, default: "6-10" },
        vzor: { type: String, default: "" },
    },
    computed: {
        titleText() {
            const prva = String(this.zadanie || "")
                .trim()
                .split(/[.\n,]/)[0]
                .trim();
            if (!prva) return this.$t("ai_offer.preview_no_title");
            const text = prva.length > 70 ? prva.slice(0, 70) + "…" : prva;
            return text.charAt(0).toUpperCase() + text.slice(1);
        },
        totalText() {
            const n = Number(String(this.rozpocet || "").replace(/\s/g, ""));
            if (!n) return "…";
            return "cca " + n.toLocaleString("sk-SK") + " " + this.mena;
        },
        rows() {
            const known = this.polozky.slice(0, 8).map((p) => ({ key: "p" + p.id, nazov: p.nazov, known: true }));
            const spolu = Math.max(known.length + 1, POCET_RIADKOV[this.pocet] || 6);
            const out = known.slice();
            for (let i = known.length; i < spolu; i++) {
                out.push({ key: "s" + i, known: false, width: 38 + ((i * 17) % 40) + "%" });
            }
            return out;
        },
    },
};
</script>

<style lang="scss" scoped>
.ai-preview {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    font-family: Roboto, sans-serif;
}
.ai-preview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.ai-preview-label {
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font: 700 11.5px Roboto, sans-serif;
    color: #212541;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    box-shadow: 0 6px 16px rgba(33, 37, 65, 0.15);
}
.ai-preview-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 0;
    background: #fff;
    color: #4a5164;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 16px rgba(33, 37, 65, 0.12);
}
.mode-inline {
    .ai-preview-label,
    .ai-preview-close {
        box-shadow: none;
        background: #f1f4f7;
    }
    .ai-paper {
        box-shadow: none;
        border: 1.5px solid #e3e8ee;
    }
}
.ai-paper {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(20, 22, 45, 0.4);
}
.ai-paper-top {
    padding: 20px 22px 12px;
}
.ai-paper-firm {
    font: 800 13px Montserrat, sans-serif;
    color: #212541;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.ai-paper-intro {
    padding: 0 22px 16px;
}
.ai-paper-kicker {
    font: 700 10.5px Roboto, sans-serif;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #c81f45;
}
.ai-paper-title {
    font: 800 19px/1.25 Montserrat, sans-serif;
    color: #212541;
    margin-top: 5px;
    transition: color 0.3s;
}
.ai-paper-client {
    font: 500 12.5px Roboto, sans-serif;
    color: #374051;
    margin-top: 6px;
}
.ai-paper-vzor {
    margin-top: 6px;
    font: 500 11.5px Roboto, sans-serif;
    color: #4353a4;
}
.ai-paper-boxes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 14px;
}
.ai-paper-box {
    border-radius: 10px;
    padding: 9px 11px;
    background: #f4f6f9;
    color: #212541;
    span {
        display: block;
        font: 600 10.5px Roboto, sans-serif;
        opacity: 0.8;
    }
    b {
        display: block;
        font: 800 15px Montserrat, sans-serif;
        margin-top: 3px;
    }
    &.total {
        background: #f3385a;
        color: #fff;
    }
}
.ai-paper-rows {
    border-top: 1px solid #eef1f5;
}
.ai-paper-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 22px;
    border-bottom: 1px solid #eef1f5;
    animation: ai-paper-in 0.35s ease-out both;
    .n {
        flex: 1;
        font: 700 12.5px Montserrat, sans-serif;
        color: #212541;
    }
    .src {
        font: 500 11px Roboto, sans-serif;
        color: #127a64;
        background: #eafbf6;
        border-radius: 999px;
        padding: 2px 8px;
    }
    .skel {
        height: 10px;
        border-radius: 5px;
        background: #eef1f5;
        &.short {
            width: 54px;
            margin-left: auto;
        }
    }
}
.ai-paper-note {
    margin: 0;
    padding: 12px 22px 16px;
    font: 400 11.5px/1.5 Roboto, sans-serif;
    color: #5b6275;
}
@keyframes ai-paper-in {
    from {
        opacity: 0;
        transform: translateX(-8px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
</style>
